import { Injectable } from '@angular/core';
import SendbirdChat, { ConnectionHandler, PushTriggerOption, SendbirdError, User, UserUpdateParams } from '@sendbird/chat';
import { GroupChannel, GroupChannelCollection, GroupChannelCollectionEventHandler, GroupChannelCollectionParams, GroupChannelCreateParams, GroupChannelFilter, GroupChannelListOrder, GroupChannelModule, PublicChannelFilter, SuperChannelFilter, UnreadChannelFilter } from '@sendbird/chat/groupChannel';
import { environment } from 'src/environments/environment';
import { RequestService } from './request.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { NotificationService } from './notification.service';
import { LoaderService } from './loader.service';
import { FileMessageCreateParams, FileMessageUpdateParams, MessageSearchOrder, MessageSearchQueryParams, ThreadedMessageListParams, UserMessageUpdateParams } from '@sendbird/chat/message';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DemoDataService } from './demo-data.service';

export interface Channel {
    name: string;
    createdAt: string;
    creator: { _iid: string, userId: string, nickname: string };
    members: any;
    joinedMemberCount: number;
    isPublic: boolean;
    status: string;
    description: string;
    _id: string;
    unreadMessageCount: number;
    url: string;
}

export interface CreateChannel {
    name: string;
    members: any;
    isPublic: boolean;
    isDistinct: boolean;
    data: string;
    type: string;
    channelUrl: string;
    operatorIds: any;
    isSuper: boolean;
}

export interface Message {
    message: string;
    createdAt: string;
    reactions: any;
    messageId: string;
    metadata: any;
    createdTimeAgo: string;
    isSameDay: boolean;

    image: string;
    name: string;
    removedBy: string;
}

export enum Status {
    ACTIVE = 'Active',
    // INACTIVE = 'Inactive',
    ARCHIVED = 'Archived',
}

export enum Type {
    PRIVATE = 'Private',
    PUBLIC = 'Public',
}

@Injectable({
    providedIn: 'root',
})
export class SendbirdService {

    public sendbirdToken: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public isConnectedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    // public reloadMessages: Subject<boolean> = new Subject<boolean>();
    // public leaveSendbird: boolean = false;
    public channelCollection: GroupChannelCollection;
    public channelCollectionPendingApproval: GroupChannelCollection;
    // public globalChannelHandler: GroupChannelCollectionEventHandler;
    public groupChannelHandler: GroupChannelCollectionEventHandler;
    public communityChannelHandler: GroupChannelCollectionEventHandler;
    public communityChannelPendingApprovalHandler: GroupChannelCollectionEventHandler;
    public fileUploadProgress: Subject<number> = new Subject<number>();
    public fileUploadRequestId: string = undefined;
    public channelId: string = '';
    public dndChannel: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public dndChannels: any = [];

    private token: string = '';
    private sendbird!: any;
    // private storageKey = 'unreadChats';

    constructor(private requestService: RequestService, private notificationService: NotificationService, private loader: LoaderService, private http: HttpClient, private demoDataService: DemoDataService) {
        const APP_ID = environment.sendBird;
        this.sendbird = SendbirdChat.init({
            appId: APP_ID,
            modules: [new GroupChannelModule()],
            // localCacheEnabled: true,
        });
    }

    setToken(token: string) {
        this.token = token;
    }

    /**
     * Returns true when using demo user/token so we skip real Sendbird API calls.
     * Channels and messages are served from DemoDataService in that case.
     */
    private isDemoMode(userId?: string, token?: string): boolean {
        const id = userId ?? this.requestService.currentUser?._id;
        const t = token ?? this.token;
        return id === 'demo_user_123' || (typeof t === 'string' && t.startsWith('demo_'));
    }

    getToken(userId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `sendbird/user/${userId}/token`, (data, error) => {
                if (data) {
                    resolve(data.results?.token);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    async connectWithAccessToken(userId: string, token: string): Promise<boolean> {
        // Demo mode: skip real Sendbird connection; channels come from DemoDataService
        if (this.isDemoMode(userId, token)) {
            this.token = token;
            this.isConnectedSubject.next(true);
            return true;
        }
        try {
            const connectionHandler: ConnectionHandler = new ConnectionHandler({
                onConnected: () => {
                    // console.log('sendbird connnected')
                    this.isConnectedSubject.next(true);
                },
                onDisconnected: async () => {
                    // console.log('sendbird disconnected');
                    if (this.channelCollection) {
                        this.channelCollection.dispose();
                        this.channelCollection.setGroupChannelCollectionHandler(null);
                    }
                    if (this.channelCollectionPendingApproval) {
                        this.channelCollectionPendingApproval.dispose();
                        this.channelCollectionPendingApproval.setGroupChannelCollectionHandler(null);
                    }
                    // if (!this.leaveSendbird) {
                    this.loader.display(true);
                    await this.sendbird.connect(userId, token);
                    this.loader.display(false);
                    // }
                },
                onReconnectStarted: () => { },
                onReconnectSucceeded: () => {
                    // console.log('sendbird reconnection successful');
                    // this.reloadMessages.next(true);
                },
                onReconnectFailed: () => {
                    // console.log('sendbird reconnection failed');
                },
            });

            this.sendbird.addConnectionHandler('sendbird_handler', connectionHandler);

            // console.log(`User connected to Sendbird: ${userId}`);
            // this.sendbird.setAutoBackgroundDetection(true);

            await this.sendbird.connect(userId, token);
            // console.log(`User connected to Sendbird: ${userId}`);

            return true;
        } catch (error) {
            console.error('Failed to connect user:', error);
            return false;
        }
    }

    async disconnect() {
        if (this.isDemoMode()) {
            this.isConnectedSubject.next(false);
            return;
        }
        try {
            // this.leaveSendbird = true;
            if (this.sendbird) {
                await this.sendbird.disconnect((response, error) => {
                    if (error) {
                        console.error("Error disconnecting", error);
                        return;
                    }
                    // console.log("Successfully disconnected from Sendbird");
                });
            }
        }
        catch (e) {
            console.log(e);
        }
    }

    getSendbird() {
        return this.sendbird;
    }

    async updateUser(nickName, profileUrl = `${environment.serverHostUrl}assets/images/profile.png`) {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const params: UserUpdateParams = {
                    nickname: nickName,
                    profileUrl: profileUrl,
                };
                const user = await this.sendbird.updateCurrentUserInfo(params);
                return user;
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    // async createCollection() {
    //     const isConnected = await this.ensureConnection();

    //     if (isConnected) {
    //         try {
    //             const groupChannelFilter: GroupChannelFilter = new GroupChannelFilter();
    //             groupChannelFilter.includeEmpty = true;

    //             const params: GroupChannelCollectionParams = {
    //                 filter: groupChannelFilter,
    //             };

    //             this.channelCollection = this.sendbird.groupChannel.createGroupChannelCollection(params);
    //             await this.channelCollection.loadMore()
    //             // console.log('this.channelCollection', this.channelCollection, await this.channelCollection.loadMore())
    //         }
    //         catch (e) {
    //             console.log(e);
    //         }
    //     }
    // }

    async getChannels(searchText: string = '', limit: number = 20, type: string = '', status: string[] = [], channelIds?: string[], getOnePage: boolean = true, order = GroupChannelListOrder.LATEST_LAST_MESSAGE): Promise<GroupChannel[]> {
        console.log('[SendbirdService] getChannels called - using demo data', { searchText, limit, type, status });
        // Directly use demo data - no API calls
        try {
            const demoChannels = await this.demoDataService.getSendbirdChannelsDemo(searchText, limit, type, status);
            // Create a mock collection object so hasMore works
            this.channelCollection = {
                hasMore: false,
                loadMore: async () => [],
                setGroupChannelCollectionHandler: () => {},
                dispose: () => {}
            } as any;
            return this.adjustChannel(demoChannels);
        } catch (error) {
            console.error('[SendbirdService] getChannels - Error getting demo data:', error);
            // Create a mock collection object so hasMore works
            this.channelCollection = {
                hasMore: false,
                loadMore: async () => [],
                setGroupChannelCollectionHandler: () => {},
                dispose: () => {}
            } as any;
            return [];
        }
    }

    async loadMoreChannels() {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                let channels: GroupChannel[] = [];
                if (this.channelCollection?.hasMore) {
                    channels = await this.channelCollection.loadMore();
                    // channels = this.updateMemberCount(channels);
                    channels = this.adjustChannel(channels);
                    // console.log('collection', channels);
                }
                return channels;
            }
            catch (e) {
                console.log(e);
                return [];
            }
        }
    }

    // updateMemberCount(channels) {
    //     return channels.map((i: any) => {
    //         let nonAdminMembers = i.members.filter(k => k.metaData?.role != environment.customKeys.roleAdmin);
    //         i.nonAdminMembers = nonAdminMembers;
    //         i.nonAdminMemberCount = nonAdminMembers.length;
    //         // if (i.data) {
    //         // if (typeof i.data == 'string')
    //         // i.data = JSON.parse(i.data);
    //         // if (i.data.type == 'one-one') {
    //         // console.log('one', this.requestService.currentUser)
    //         // let findMe = i.members.findIndex(k => k.userId == this.requestService.currentUser._id);
    //         // if (findMe != -1 && i.members.length == 2) {
    //         //     if (findMe == 0) {
    //         //         i.name = i.members[1].nickname;
    //         //     }
    //         //     else {
    //         //         i.name = i.members[0].nickname;
    //         //     }
    //         // }
    //         // }
    //         // }
    //         // console.log(i)
    //         return i;
    //     });
    // }

    adjustChannel(channels) {
        if (!channels || !Array.isArray(channels)) {
            console.warn('[SendbirdService] adjustChannel - channels is not an array:', channels);
            return [];
        }
        // Ensure dndChannels is an array
        if (!this.dndChannels || !Array.isArray(this.dndChannels)) {
            this.dndChannels = [];
        }
        return channels.map((i: any) => {
            // Ensure members is an array
            if (!i.members || !Array.isArray(i.members)) {
                i.members = [];
            }
            let nonAdminMembers = i.members.filter(k => k?.metaData?.role != environment.customKeys.roleAdmin);
            i.nonAdminMembers = nonAdminMembers;
            i.nonAdminMemberCount = nonAdminMembers.length;
            i.dnd = this.dndChannels.find(o => o.channelId == i.url)?.dnd;
            // i.other = i.members.find(k => k.userId != this.requestService.currentUser._id);
            // console.log(i)
            if (!i.isPublic && i.isDistinct) {
                i.other = i.members.find(k => k?.userId != this.requestService.currentUser?._id);
                if (i.other) {
                    i.other = {
                        ...i.other,
                        metaData: i.other.metaData || {},
                        plainProfileUrl: i.other.plainProfileUrl || '',
                    };
                }
            }
            return i;
        });
    }

    async createChannel(channel: CreateChannel): Promise<GroupChannel> {
        // Use demo data if available
        if (this.demoDataService) {
            console.log('[SendbirdService] createChannel - using demo data', channel);
            try {
                const createdChannel = await this.demoDataService.createSendbirdChannelDemo(channel);
                // Convert to GroupChannel-like object
                return this.adjustChannel([createdChannel])[0] as any;
            } catch (e) {
                console.error('[SendbirdService] createChannel - demo data error:', e);
                throw e;
            }
        }

        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const params: GroupChannelCreateParams = {
                    invitedUserIds: channel.members,
                    name: channel.name,
                    operatorUserIds: [],
                    data: channel.data,
                    customType: channel.type,
                    isDistinct: channel.isDistinct,
                    channelUrl: channel.channelUrl,
                    isPublic: channel.isPublic,
                    isSuper: channel.isSuper,
                };
                // console.log('params', params)
                const createdChannel: GroupChannel = await this.sendbird.groupChannel.createChannel(params);
                return createdChannel;
            }
            catch (e) {
                console.log(e);
                throw e;
            }
        }
        
        throw new Error('Not connected to Sendbird');
    }

    async createChannelByApi(channel: CreateChannel): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird/user', 'createGroupChannel', {
                user_ids: channel.members,
                name: channel.name,
                operator_ids: [],
                data: channel.data,
                custom_type: channel.type,
                is_distinct: channel.isDistinct,
                channel_url: channel.channelUrl,
                is_public: channel.isPublic,
                is_super: channel.isSuper,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    async updateChannel(channelUrl: string, params: GroupChannelCreateParams): Promise<GroupChannel> {
        if (channelUrl) {
            const isConnected = await this.ensureConnection();

            if (isConnected) {
                try {
                    const groupChannel = await this.getChannel(channelUrl);

                    const updatedChannel = await groupChannel.updateChannel(params,
                        (updatedChannel, error) => {
                            if (error) {
                                throw new Error('Failed to update channel: ' + error);
                            }
                            return updatedChannel;
                        }
                    );
                    // console.log(updatedChannel)
                    return updatedChannel;
                }
                catch (e) {
                    console.log(e);
                }
            }
        }
        return null;
    }

    async fetchUsersBySearchText(searchText: string, limit: number = 20): Promise<any> {
        // Use demo data if available
        if (this.demoDataService) {
            console.log('[SendbirdService] fetchUsersBySearchText - using demo data', { searchText, limit });
            try {
                const users = await this.demoDataService.getSendbirdUsersBySearchTextDemo(searchText, limit);
                return users || [];
            } catch (e) {
                console.error('[SendbirdService] fetchUsersBySearchText - demo data error:', e);
                return [];
            }
        }

        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const query = await this.sendbird.createApplicationUserListQuery({
                    metaDataKeyFilter: 'role',
                    metaDataValuesFilter: [environment.customKeys.roleAgencyAdmin, environment.customKeys.roleAgent]
                });
                query.nicknameStartsWithFilter = searchText;
                // query.nicknameContainsFilter = searchText;
                query.limit = limit;

                const users = await query.next();
                return users || [];
            }
            catch (e) {
                console.log('[SendbirdService] fetchUsersBySearchText - error:', e);
                return [];
            }
        }
        
        return [];
    }

    async getRegisteredUsers(limit: number = 100): Promise<string[]> {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const query = this.sendbird.createApplicationUserListQuery();
                query.limit = limit;

                let allUsers = [];
                // console.log('query', query)
                while (query.hasNext) {
                    await new Promise(async (resolve) => {
                        let users = await query.next();
                        allUsers = allUsers.concat(users);
                        resolve(users);
                    });
                }

                return allUsers;
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    async getChannel(channelUrl: string): Promise<any> {
        console.log('[SendbirdService] getChannel called - using demo data', { channelUrl });
        // Directly use demo data - no API calls
        return await this.demoDataService.getSendbirdChannelDemo(channelUrl);
    }

    async getMessages(channel): Promise<any> {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const query = channel.createPreviousMessageListQuery();
                query.limit = 30; // Number of messages to fetch
                query.reverse = false; // Set to `true` to get messages in reverse order

                return await query.load();
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    async getMessagesByTimestamp(channel, lastTimestamp, limit): Promise<any> {
        if (channel) {
            // Directly use demo data - no API calls
            // If channel has getMessagesByTimestamp method (real Sendbird channel), use it
            // Otherwise, return empty array (demo channel will have mock method that returns empty array)
            try {
                if (typeof channel.getMessagesByTimestamp === 'function') {
                    const params = {
                        includeThreadInfo: true,
                        prevResultSize: limit,
                        reverse: false,
                        includeReactions: true,
                    };
                    let messages = await channel.getMessagesByTimestamp(lastTimestamp, params);
                    return messages;
                } else {
                    // Demo channel - return empty array
                    return [];
                }
            }
            catch (e) {
                console.log('[SendbirdService] getMessagesByTimestamp error:', e);
                return [];
            }
        }
        return [];
    }

    async getMessagesBeforeAndAfterThisMessageId(targetMessageId: string, channel): Promise<any> {
        if (channel) {
            const isConnected = await this.ensureConnection();

            if (isConnected) {
                return new Promise((resolve, reject) => {
                    try {
                        const previousParams = {
                            previousResultSize: 5,
                            nextResultSize: 0,
                            isInclusive: false,
                            reverse: true,
                        };

                        const nextParams = {
                            previousResultSize: 0,
                            nextResultSize: 30,
                            isInclusive: true,
                            reverse: false,
                        };

                        Promise.all([
                            channel.getMessagesByMessageId(targetMessageId, previousParams),
                            channel.getMessagesByMessageId(targetMessageId, nextParams)
                        ]).then(([prevMessages, nextMessages]) => {
                            const merged = [...prevMessages.reverse(), ...nextMessages];
                            resolve(merged);
                        })
                            .catch((error) => {
                                reject(error);
                            });
                    }
                    catch (e) {
                        console.log(e);
                    }
                });
            }
        }
    }

    async fetchAllMessagesAfter(channel, startingMessageId, batchSize = 30) {
        let allMessages = [];
        let done = false;
        let lastMessageId = startingMessageId;

        while (!done) {
            const params = {
                previousResultSize: 0,
                nextResultSize: batchSize,
                isInclusive: false,
                reverse: false
            };

            const messages = await channel.getMessagesByMessageId(lastMessageId, params);

            if (messages.length === 0) {
                done = true;
            } else {
                allMessages = allMessages.concat(messages);
                // Set up for the next batch
                lastMessageId = messages[messages.length - 1].messageId;
            }
        }

        return allMessages;
    }

    async sendMessage(message: string, channel, hasParentId?: number, mentionedUserIds?: string[]): Promise<any> {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const params: any = {
                    message: message,
                    // customType: channel.isPublic ? Type.PUBLIC : Type.PRIVATE,
                    parentMessageId: hasParentId,
                };

                if (mentionedUserIds?.length) {
                    params.mentionedUserIds = mentionedUserIds;
                }

                return new Promise(async (resolve, reject) => {
                    await channel.sendUserMessage(params).onSucceeded((message) => {
                        // console.log("Message sent successfully:", message);
                        resolve(message);
                    }).onFailed((error) => {
                        console.error("Message failed to send:", error);
                        reject(error);
                    });
                });
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    async updateMessage(messageId: string, message: string, channel, mentionedUserIds?: string[]): Promise<any> {
        // Demo mode: use demo channel method directly if available
        if (channel && typeof channel.updateUserMessage === 'function') {
            try {
                const params: any = {
                    message: message
                };

                if (mentionedUserIds?.length) {
                    params.mentionedUserIds = mentionedUserIds;
                }

                return await channel.updateUserMessage(messageId, params);
            }
            catch (e) {
                console.log(e);
                throw e;
            }
        }

        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const params: UserMessageUpdateParams = {
                    message: message
                };

                if (mentionedUserIds?.length) {
                    params.mentionedUserIds = mentionedUserIds;
                }

                return await channel.updateUserMessage(messageId, params)
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    async updateFileMessage(messageId: string, message: string, channel): Promise<any> {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const params: FileMessageUpdateParams = {
                    // data: JSON.stringify({ description: message })
                    customType: message,
                };

                return await channel.updateFileMessage(messageId, params)
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    async updateFileMessageByApi(messageId: string, message: string, fileUrl: string, channelUrl: string, fileType: string, mentionedUserIds?: string[]): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird', 'user/update/message', {
                data: JSON.stringify({ fileName: this.extractFileName(fileUrl), fileType: fileType }),
                url: fileUrl,
                custom_type: message,
                // message: message,
                message_id: messageId,
                channel_type: 'group_channels',
                channel_url: channelUrl,
                message_type: 'FILE',
                mentioned_user_ids: mentionedUserIds,
            }, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    async searchMessages(searchText: string, channelUrl: string, limit: number = 99, isActiveOn: boolean, targetFields: string[]): Promise<any> {
        const isConnected = await this.ensureConnection();
        if (!isConnected) return [];

        try {
            const params: MessageSearchQueryParams = {
                keyword: (searchText?.includes('\'') || searchText?.includes('"')) ? searchText : searchText + '~',
                limit: limit,
                exactMatch: false,
                messageTimestampFrom: null,
                messageTimestampTo: null,
                order: MessageSearchOrder.SCORE,
                reverse: false,
                advancedQuery: !searchText?.includes('"'),
                channelCustomType: isActiveOn ? Status.ACTIVE : Status.ARCHIVED,
            };

            if (targetFields.length) {
                params.targetFields = targetFields;
            }

            if (channelUrl) {
                params.channelUrl = channelUrl;
            }

            const query = this.sendbird.createMessageSearchQuery(params);

            let allMessages: any[] = [];

            while (query.hasNext) {
                const messages = await query.next();
                allMessages = allMessages.concat(messages);
            }

            return allMessages;
        }
        catch (e) {
            console.log(e);
            return [];
        }
    }

    async removeHandler(handlerId: string) {
        if (handlerId) {
            const isConnected = await this.ensureConnection();

            if (isConnected) {
                try {
                    this.sendbird.groupChannel.removeGroupChannelHandler(handlerId);
                }
                catch (e) {
                    console.log(e);
                }
            }
        }
    }

    async deleteMessage(message: any, channel): Promise<any> {
        return new Promise(async (resolve, reject) => {
            // Demo mode: use demo channel method directly if available
            if (channel && typeof channel.deleteMessage === 'function') {
                try {
                    await channel.deleteMessage(message);
                    resolve(true);
                }
                catch (e) {
                    console.log(e);
                    reject(e);
                }
                return;
            }

            const isConnected = await this.ensureConnection();

            if (isConnected) {
                try {
                    let d = await channel.deleteMessage(message);
                    resolve(d);
                }
                catch (e) {
                    console.log(e);
                    reject(e);
                }
            }
            else {
                reject();
            }
        });
    }

    async markMessageAsDeleted(message: any, channel, removedBy) {
        const isConnected = await this.ensureConnection();

        if (isConnected && channel && message) {
            try {
                const params: any = {};
                // params.customType = 'removed';
                let existingData: any = {};
                if (message.data) {
                    try {
                        existingData = typeof message.data === 'string' ? JSON.parse(message.data) : { ...message.data };
                    } catch (e) {
                        existingData = {};
                    }
                }
                existingData.removedBy = removedBy;
                params.data = JSON.stringify(existingData);

                return await channel.updateUserMessage(message.messageId, params);
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    async markMessageAsRestored(message: any, channel) {
        const isConnected = await this.ensureConnection();

        if (isConnected && message && channel) {
            try {
                const params: any = {};
                // params.customType = channel.isPublic ? Type.PUBLIC : Type.PRIVATE;

                if (message.data) {
                    try {
                        const dataObj: any = typeof message.data === 'string' ? JSON.parse(message.data) : { ...message.data };
                        delete dataObj.removedBy;
                        if (Object.keys(dataObj).length) {
                            params.data = JSON.stringify(dataObj);
                        } else {
                            params.data = '';
                        }
                    } catch (e) {
                        params.data = '';
                    }
                }
                // params.data = '';

                return await channel.updateUserMessage(message.messageId, params);
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    async markFileMessageAsDeleted(message: any, channel, removedBy) {
        return new Promise((resolve, reject) => {
            let existingData: any = {};
            if (message?.data) {
                try {
                    existingData = typeof message.data === 'string' ? JSON.parse(message.data) : { ...message.data };
                } catch (e) {
                    existingData = {};
                }
            }
            existingData.removedBy = removedBy;

            this.requestService.postRequest('sendbird', 'user/update/message', {
                data: JSON.stringify(existingData),
                // url: fileUrl,
                // custom_type: JSON.stringify({ description: message }),
                // message: message,
                message_id: message.messageId,
                channel_type: 'group_channels',
                channel_url: channel.url,
                message_type: 'FILE',
            }, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    async markFileMessageAsRestored(message: any, channel) {
        const params: any = {};
        // params.customType = channel.isPublic ? Type.PUBLIC : Type.PRIVATE;

        if (message.data) {
            // let data = JSON.parse(message.data);
            delete message.data.removedBy;
            if (Object.keys(message.data).length)
                params.data = JSON.stringify(message.data);
            else
                params.data = '';
        }

        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird', 'user/update/message', {
                data: params.data,
                // url: fileUrl,
                // custom_type: JSON.stringify({ description: message }),
                // message: message,
                message_id: message.messageId,
                channel_type: 'group_channels',
                channel_url: channel.url,
                message_type: 'FILE',
            }, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    async markMessageAsDeletedByApi(messageId: string, channelUrl: string, removedBy): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird', 'update/messagemetadata', {
                channel_url: channelUrl,
                message_id: messageId,
                channel_type: 'group_channels',
                upsert: true,
                mode: 'add',
                sorted_metaarray: [
                    {
                        key: 'customType',
                        value: ['removed'],
                    },
                    {
                        key: 'removedBy',
                        value: [removedBy],
                    }
                ],
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    async markMessageAsRestoredByApi(messageId: string, channelUrl: string, removedBy): Promise<any> {
        return new Promise((resolve, reject) => {
            const params: any = {};
            params.customType = '';
            params.data = '';

            this.requestService.postRequest('sendbird', 'update/messagemetadata', {
                channel_url: channelUrl,
                message_id: messageId,
                channel_type: 'group_channels',
                upsert: true,
                mode: 'remove',
                sorted_metaarray: [
                    {
                        key: 'customType',
                        value: ['removed'],
                    },
                    {
                        key: 'removedBy',
                        value: [removedBy],
                    }
                ],
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    handleError(error: SendbirdError) {
        if (error) {
            if (error.code == 400108) {
                return 'Not Authorized';
            }
            else {
                return error.message;
            }
        }
    }

    createUser(userId, userName, userProfilePic, role, agencyName): Promise<any> {
        return new Promise((resolve, reject) => {
            if (userId && userName) {
                this.requestService.postRequest('sendbird', 'user/create', {
                    userId: userId,
                    profile: userProfilePic,
                    name: userName,
                    metadata: {
                        role: role,
                        agency: agencyName,
                    },
                }, (data, error) => {
                    if (data) {
                        resolve(data);
                    }
                    if (error) {
                        reject(error);
                    }
                });
            }
            else {
                reject('Please try again later.');
            }
        });
    }

    async getChannelOperators(channel) {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            const query = channel.createOperatorListQuery();
            try {
                const operators = await query.next();
                // console.log("Operators:", operators);
                return operators;
            } catch (error) {
                console.error("Failed to retrieve operators:", error);
            }
        }
    }

    assignOperators(userIds: string[], channelUrl: string): Promise<any> {
        console.log('[SendbirdService] assignOperators called with:', { userIds, channelUrl });
        return new Promise((resolve, reject) => {
            this.requestService.postRequest(`sendbird/user/register_operators_group_channel`, '', {
                operator_ids: userIds,
                channel_url: channelUrl,
            }, (data, error) => {
                console.log('[SendbirdService] assignOperators response:', { data, error });
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    unassignOperators(userIds: string[], channelUrl: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest(`sendbird/user/unregister_group_channel`, '', {
                operator_ids: userIds,
                channel_url: channelUrl,
                delete_all: false,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    async markAsRead(channel) {
        if (channel) {
            const isConnected = await this.ensureConnection();

            if (isConnected) {
                try {
                    channel.markAsRead().catch((error) => {
                        console.error("mark read failed:", error);
                    }).finally(() => {
                        this.notificationService.showChatUnRead.next(true);
                    });
                }
                catch (e) {
                    console.log(e);
                }
            }
        }
    }

    listChannels(searchText: string = '', limit: number = 20, type: string = '', status: string = '', next: string = '', distinctMode: string = 'nondistinct', membersIdsIn: string): Promise<any> {
        return new Promise((resolve, reject) => {
            let postData: any = {
                limit: limit + '',
                show_empty: true,
                order: 'chronological',
                show_member: true,
                show_metadata: true,
                distinct_mode: distinctMode,
            };

            if (status) {
                postData.custom_types = [status];
            }

            postData.public_mode = type == 'Public' ? PublicChannelFilter.PUBLIC : type == Type.PRIVATE ? PublicChannelFilter.PRIVATE : PublicChannelFilter.ALL;

            if (searchText) {
                postData.name_contains = searchText;
                // postData.members_include_in = searchText;
                // postData.query_type = 'OR';
            }

            if (next != '') {
                {
                    postData.token = next;
                }
            }

            if (membersIdsIn) {
                postData.members_include_in = membersIdsIn;
            }

            this.requestService.postRequest('sendbird/user/listgroupchannel', '', postData, (data, error) => {
                if (data?.status) {
                    data.results.channels = data.results.channels.map((i: any) => {
                        // let nonAdminMembers = i.members.filter(k => k.metadata?.role != environment.customKeys.roleAdmin);
                        // i.nonAdminMembers = nonAdminMembers;
                        // i.nonAdminMemberCount = nonAdminMembers.length;
                        return this.standardizeChannel(i);
                        // return i;
                    });
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    listOneOneChannels(searchText: string = '', limit: number = 20, type: string = '', status: string = '', next: string = '', distinctMode: string = 'nondistinct', currentUserId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            let postData: any = {
                limit: limit + '',
                show_empty: true,
                order: 'chronological',
                show_member: true,
                show_metadata: true,
                distinct_mode: distinctMode,
                members_include_in: `${currentUserId} `,
            };

            if (status) {
                postData.custom_types = [status];
            }

            postData.public_mode = type == 'Public' ? PublicChannelFilter.PUBLIC : type == Type.PRIVATE ? PublicChannelFilter.PRIVATE : PublicChannelFilter.ALL;

            if (searchText) {
                postData.name_contains = searchText;
                // postData.members_include_in = searchText;
                // postData.query_type = 'OR';
            }

            if (next != '') {
                {
                    postData.token = next;
                }
            }

            this.requestService.postRequest('sendbird/user/listgroupchannel', '', postData, (data, error) => {
                if (data?.status) {
                    data.results.channels = data.results.channels.map((i: any) => {
                        // let nonAdminMembers = i.members.filter(k => k.metadata?.role != environment.customKeys.roleAdmin);
                        // i.nonAdminMembers = nonAdminMembers;
                        // i.nonAdminMemberCount = nonAdminMembers.length;
                        return this.standardizeChannel(i);
                        // return i;
                    });
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    async checkIfOneOneChannelExistsByMember(memberId: string, currentUserId: string): Promise<GroupChannel[]> {
        // const isConnected = await this.ensureConnection();

        // if (isConnected) {
        //     try {
        //         const params: GroupChannelListQueryParams = {
        //             userIdsFilter: {
        //                 userIds: [memberId, currentUserId],
        //                 includeMode: true,
        //                 queryType: QueryType.AND,
        //             },
        //             publicChannelFilter: PublicChannelFilter.PRIVATE,

        //         };
        //         const query: GroupChannelListQuery = this.sendbird.groupChannel.createMyGroupChannelListQuery(params);
        //         return await query.next();
        //     }
        //     catch (e) {
        //         console.log(e);
        //     }
        // }
        return new Promise((resolve, reject) => {
            let postData: any = {
                limit: 1,
                show_empty: true,
                show_member: true,
                show_metadata: true,
                distinct_mode: 'distinct',
                public_mode: PublicChannelFilter.PRIVATE,
                members_exactly_in: `${memberId},${currentUserId} `,
            };

            this.requestService.postRequest('sendbird/user/listgroupchannel', '', postData, (data, error) => {
                if (data?.status) {
                    resolve(data.results?.channels || []);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    standardizeChannel(channel) {
        channel.url = channel.channel_url || channel.url;
        channel.customType = channel.custom_type || channel.customType;
        channel.isPublic = channel.is_public || channel.isPublic;
        channel.data = channel.data ? JSON.parse(channel.data) : undefined;
        channel.joinedMemberCount = channel.joined_member_count || channel.joinedMemberCount;
        channel.unreadMessageCount = channel.unread_message_count || channel.unreadMessageCount;
        channel.creator = channel.created_by || channel.createdBy;
        channel.createdAt = (channel.created_at || channel.createdAt) * 1000;
        channel.members = channel.members.map(m => ({
            ...m,
            userId: m.user_id || m.userId
        }));
        channel.nonAdminMembers = channel.nonAdminMembers.map(m => ({
            ...m,
            userId: m.user_id || m.userId
        }));
        if (!channel.is_public && channel.is_distinct) {
            channel.other = channel.members.find(k => k.userId != this.requestService.currentUser._id);
            channel.other = {
                ...channel.other,
                metaData: channel.other.metadata,
                plainProfileUrl: channel.other.profile_url,
            };
        }
        return channel;
    }

    getEmojis(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', 'sendbird/emoji/list', (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    async getOperators(channelUrl): Promise<User[]> {
        // Use demo data if available
        if (this.demoDataService) {
            console.log('[SendbirdService] getOperators - using demo data', channelUrl);
            try {
                const data = await new Promise<any>((resolve, reject) => {
                    this.requestService.postRequest(
                        'sendbird',
                        'operatorsgroupchannel/list',
                        {
                            channel_url: channelUrl,
                            limit: 100,
                            next: null,
                        },
                        (data: any, error: any) => {
                            if (error) reject(error);
                            else resolve(data);
                        }
                    );
                });

                console.log('[SendbirdService] getOperators - received data:', data);
                // Handle both wrapped and unwrapped responses
                let operators = [];
                if (data && data.results) {
                    if (data.results.operators) {
                        // Direct structure: { status: true, results: { operators: [...] } }
                        operators = data.results.operators;
                    } else if (data.results.results && data.results.results.operators) {
                        // Double-wrapped: { status: true, results: { status: true, results: { operators: [...] } } }
                        operators = data.results.results.operators;
                    }
                }
                console.log('[SendbirdService] getOperators - extracted operators:', operators);
                return operators;
            } catch (error) {
                console.error('[SendbirdService] getOperators - demo data error:', error);
                return [];
            }
        }

        let allOperators: any[] = [];

        const fetchOperators = async (next?: string): Promise<void> => {
            try {
                const data = await new Promise<any>((resolve, reject) => {
                    this.requestService.postRequest(
                        'sendbird',
                        'operatorsgroupchannel/list',
                        {
                            channel_url: channelUrl,
                            limit: 100,
                            next: next || null,
                        },
                        (data: any, error: any) => {
                            if (error) reject(error);
                            else resolve(data);
                        }
                    );
                });

                if (data) {
                    allOperators = allOperators.concat(data.results.operators);

                    if (data.results.next) {
                        await fetchOperators(data.results.next);
                    }
                }
            } catch (error) {
                console.error('Error fetching operators:', error);
                throw error; // Reject the promise with an error
            }
        };

        await fetchOperators();

        return allOperators;
    }

    getOperatorsByApi(channelUrl: string, limit: number = 100): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird', 'operatorsgroupchannel/list', {
                limit: limit,
                channel_url: channelUrl,
                // token: 0,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    inviteUsers(members: string[], inviterId: string, channelUrl: string) {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird', 'invite/member', {
                user_ids: members,
                channel_url: channelUrl,
                inviter_id: inviterId,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    removeUsers(members: string[], channelUrl: string) {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird', 'leave/channel', {
                user_ids: members,
                channel_url: channelUrl,
                reason: 'admin_removed'
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    async getAdmins(limit: number = 100) {
        console.log('[SendbirdService] getAdmins called - using demo data', { limit });
        // Directly use demo data - no API calls
        return await this.demoDataService.getSendbirdAdminsDemo(limit);
    }

    async getMessagesByApi(channelUrl, lastTimestamp, limit): Promise<any> {
        return new Promise(async (resolve, reject) => {
            this.requestService.postRequest('sendbird', 'user/listmessages', {
                message_ts: lastTimestamp,
                next_limit: limit,
                channel_url: channelUrl,
                channel_type: 'group_channels',
                with_sorted_meta_array: true,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    async sendMessageByApi(channelUrl, message, messageType, userId): Promise<any> {
        return new Promise(async (resolve, reject) => {
            this.requestService.postRequest('sendbird', 'user/sendmessage', {
                channel_url: channelUrl,
                channel_type: 'group_channels',
                message: message,
                message_type: messageType,
                user_id: userId,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    async registerToken(userId: string, firebaseToken: string): Promise<any> {
        return new Promise(async (resolve, reject) => {
            this.requestService.postRequest('sendbird', 'registeruserdevice', {
                user_id: userId,
                token_type: 'gcm',
                gcm_reg_token: firebaseToken,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    async unRegisterToken(userId: string, firebaseToken: string): Promise<any> {
        return new Promise(async (resolve, reject) => {
            this.requestService.postRequest('sendbird', 'unregisteruserdevice', {
                user_id: userId,
                token_type: 'gcm',
                token: firebaseToken,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    async updateChannelByApi(name: string, channelUrl: string, customType: string, isPublic: boolean, data: string): Promise<any> {
        return new Promise(async (resolve, reject) => {
            this.requestService.postRequest('sendbird', 'user/updateGroupChannel', {
                name: name,
                custom_type: customType,
                channel_url: channelUrl,
                is_public: isPublic,
                data: data,
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    // getUnreadChats(): string[] {
    //     // if (!localStorage.getItem(this.storageKey)) {
    //     //     localStorage.setItem(this.storageKey, '[]');
    //     // }
    //     const unreadChats = localStorage.getItem(this.storageKey);
    //     return unreadChats ? JSON.parse(unreadChats) : [];
    // }

    // addUnreadChat(channelId: string): void {
    //     const unreadChats = this.getUnreadChats();
    //     if (!unreadChats.includes(channelId)) {
    //         unreadChats.push(channelId);
    //         localStorage.setItem(this.storageKey, JSON.stringify(unreadChats));
    //         this.notificationService.showChatUnRead.next(unreadChats);
    //     }
    // }

    // removeUnreadChat(channelId: string): void {
    //     const unreadChats = this.getUnreadChats();
    //     const updatedChats = unreadChats.filter((id) => id !== channelId);
    //     localStorage.setItem(this.storageKey, JSON.stringify(updatedChats));
    //     this.notificationService.showChatUnRead.next(updatedChats);
    // }

    // clearUnreadChats(): void {
    //     localStorage.removeItem(this.storageKey);
    //     this.notificationService.showChatUnRead.next([]);
    // }

    async autoAcceptInvite() {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                const autoAccept = true;
                await this.sendbird.setChannelInvitationPreference(autoAccept);
            }
            catch (e) {
                console.log(e)
            }
        }
    }

    private async ensureConnection() {
        // Demo mode: no real Sendbird connection; treat as connected
        if (this.isDemoMode()) {
            return true;
        }
        console.log('[SendbirdService] ensureConnection called', {
            hasCurrentUser: !!this.sendbird.currentUser,
            hasToken: !!this.token,
            hasRequestUser: !!this.requestService.currentUser?._id
        });
        
        if (this.sendbird.currentUser && this.token && this.requestService.currentUser?._id) {
            // Connection is already open
            console.log('[SendbirdService] ensureConnection - already connected');
            return true;
        } else {
            console.warn('[SendbirdService] ensureConnection - Connection is not open. Attempting to reconnect...');
            if (this.requestService.currentUser?._id && this.token) {
                // Attempt to reconnect
                return new Promise((resolve) => {
                    this.sendbird.connect(this.requestService.currentUser._id, this.token, (user, error) => {
                        if (error) {
                            console.error('[SendbirdService] ensureConnection - Reconnection failed:', error);
                            resolve(false); // Return false on failure
                        } else {
                            console.log('[SendbirdService] ensureConnection - Reconnected successfully.');
                            resolve(true); // Return true on success
                        }
                    });
                });
            } else {
                console.warn('[SendbirdService] ensureConnection - Cannot reconnect: missing user ID or token');
                return false;
            }
        }
    }

    async setMyPushTriggerOption(isDnd, channel) {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                // console.log(isDnd ? PushTriggerOption.OFF : PushTriggerOption.ALL);
                channel.setMyPushTriggerOption(isDnd ? PushTriggerOption.OFF : PushTriggerOption.ALL);
            }
            catch (e) {
                console.log(e)
            }
        }
    }

    async setPushOptions(isDnd) {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                // console.log(isDnd ? PushTriggerOption.OFF : PushTriggerOption.ALL);
                this.sendbird.setPushTriggerOption(isDnd ? PushTriggerOption.OFF : PushTriggerOption.ALL);
            }
            catch (e) {
                console.log(e)
            }
        }
    }

    async addReaction(channel, message, reaction): Promise<any> {
        try {
            await channel.addReaction(message, reaction);
            // For demo mode, manually update the message object since onReactionUpdated handler might not fire
            // The addReaction method in demo channel already updates the message object, so we just need to ensure
            // the reaction format is correct for the UI
            if (message.reactions) {
                message.reactions.forEach((r: any) => {
                    // Ensure reaction has all required properties for UI
                    if (!r.hasOwnProperty('count')) {
                        r.count = r.userIds ? r.userIds.length : 1;
                    }
                    if (!r.hasOwnProperty('hasCurrentUserReacted')) {
                        r.hasCurrentUserReacted = r.userIds && r.userIds.includes('demo_user_123');
                    }
                    if (!r.hasOwnProperty('sampledUserInfoList')) {
                        r.sampledUserInfoList = r.userIds ? r.userIds.map((uid: string) => ({
                            userId: uid,
                            nickname: uid === 'demo_user_123' ? 'Demo User' : 'User',
                            plainProfileUrl: 'assets/images/profile.png'
                        })) : [];
                    }
                });
            }
            // message.applyReactionEvent(reactionEvent);
        }
        catch (e) {
            console.log(e);
        }
    }

    async removeReaction(channel, message, reaction): Promise<any> {
        try {
            await channel.deleteReaction(message, reaction);
            // For demo mode, ensure reaction format is correct after deletion
            if (message.reactions) {
                message.reactions.forEach((r: any) => {
                    if (!r.hasOwnProperty('count')) {
                        r.count = r.userIds ? r.userIds.length : 0;
                    }
                    if (!r.hasOwnProperty('hasCurrentUserReacted')) {
                        r.hasCurrentUserReacted = r.userIds && r.userIds.includes('demo_user_123');
                    }
                    if (!r.hasOwnProperty('sampledUserInfoList')) {
                        r.sampledUserInfoList = r.userIds ? r.userIds.map((uid: string) => ({
                            userId: uid,
                            nickname: uid === 'demo_user_123' ? 'Demo User' : 'User',
                            plainProfileUrl: 'assets/images/profile.png'
                        })) : [];
                    }
                });
            }
        }
        catch (e) {
            console.log(e);
        }
    }

    async uploadFile(file: any, channel) {
        const request = await channel.uploadFile({
            file,
            uploadStartedHandler: (requestId: string) => {
                // upload got started
                // console.log(requestId)
                this.fileUploadRequestId = requestId;
            },
            progressHandler: (requestId: string, progress: number, total: number) => {
                // progress in percentage = progress / total
                // console.log(progress / total)
                this.fileUploadProgress.next(progress / total);
            }
        });

        // this.fileUploadRequestId = undefined;
        return request;

        // const uploadTask = channel.sendFileMessage(params, (event) => {
        //     // Progress handler
        //     const { totalBytesSent, totalBytesExpectedToSend } = event;
        //     const progress = (totalBytesSent / totalBytesExpectedToSend) * 100;
        //     console.log(`Progress: ${ progress }% `);
        // }, (message, error) => {
        //     if (error) {
        //         console.error('Error sending file message:', error);
        //     } else {
        //         console.log('File message sent successfully:', message);
        //     }
        // });

        // const request = await uploadTask.promise;
        // console.log('File message sent:', request);
    }

    async setBackgroundState() {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                console.log('background state')
                this.sendbird.setBackgroundState();
            }
            catch (e) {
                console.log(e)
            }
        }
    }

    async setForegroundState() {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                console.log('foreground state')
                this.sendbird.setForegroundState();
            }
            catch (e) {
                console.log(e)
            }
        }
    }

    async sendFileMessage(message = '', file: any, channel, hasParentId?: number, mentionedUserIds?: string[]): Promise<any> {
        try {
            const params: FileMessageCreateParams = {
                fileUrl: file.url,
                // fileName: file.name,
                // mimeType: file.type,
                // customType: message,
                message: message,
                customType: message,
                // thumbnailSizes: [{ maxWidth: 100, maxHeight: 100 }, { maxWidth: 200, maxHeight: 200 }]
                parentMessageId: hasParentId,
                data: JSON.stringify({ fileName: file.name, fileType: file.type, fileId: file.fileId }),
            };

            if (mentionedUserIds?.length) {
                params.mentionedUserIds = mentionedUserIds;
            }

            return new Promise(async (resolve, reject) => {
                await channel.sendFileMessage(params).onSucceeded((message) => {
                    // console.log("Message sent successfully:", message);
                    resolve(message);
                }).onFailed((error) => {
                    console.error("Message failed to send:", error);
                    reject(error);
                });
            });
        }
        catch (e) {
            console.log(e);
        }
    }

    async sendGifMessage(message = '', gifData: any, channel, hasParentId?: number): Promise<any> {
        try {
            const params = {
                message: message,
                customType: 'GIF',
                parentMessageId: hasParentId,
                data: JSON.stringify({ 
                    gifUrl: gifData.gifUrl, 
                    gifTitle: gifData.gifTitle, 
                    messageType: 'gif',
                    type: 'GIF'
                }),
            };

            return new Promise(async (resolve, reject) => {
                await channel.sendUserMessage(params).onSucceeded((message) => {
                    // console.log("GIF message sent successfully:", message);
                    resolve(message);
                }).onFailed((error) => {
                    console.error("GIF message failed to send:", error);
                    reject(error);
                });
            });
        }
        catch (e) {
            console.log(e);
        }
    }

    async cancelUpload(channel, requestId = this.fileUploadRequestId) {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            if (requestId) {
                try {
                    const result = await channel.cancelUploadingFileMessage(requestId).then((result) => {
                        // console.log("File upload canceled successfully:", result);
                    })

                    // console.log(result)
                }
                catch (e) {
                    console.log(e);
                }
            }
        }
    }

    extractFileName(fileUrl: string): string {
        const parts = fileUrl.split('/');
        return parts[parts.length - 1] || 'file';
    }

    downloadSendbirdFile(fileUrl: string, apiToken: string = environment.sendBirdToken): Promise<void> {
        return new Promise((resolve, reject) => {
            const headers = new HttpHeaders({
                'Api-Token': apiToken, // Pass the API Token
            });

            // Make the HTTP GET request to download the file
            this.http
                .get(fileUrl, { headers, responseType: 'blob' }) // Response as Blob
                .subscribe({
                    next: (blob: Blob) => {
                        // Create a temporary anchor element to trigger the download
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = this.extractFileName(fileUrl); // Extract file name if possible
                        link.click();

                        // Cleanup
                        URL.revokeObjectURL(link.href);

                        resolve();
                    },
                    error: (error) => {
                        console.error('Error downloading file:', error);
                        reject(error);
                    },
                });
        });
    }

    downloadFile(fileUrl: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const headers = new HttpHeaders({});

            // Make the HTTP GET request to download the file
            this.http
                .get(fileUrl, { headers, responseType: 'blob' }) // Response as Blob
                .subscribe({
                    next: (blob: Blob) => {
                        // Create a temporary anchor element to trigger the download
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = this.extractFileName(fileUrl); // Extract file name if possible
                        link.click();

                        // Cleanup
                        URL.revokeObjectURL(link.href);

                        resolve();
                    },
                    error: (error) => {
                        console.error('Error downloading file:', error);
                        reject(error);
                    },
                });
        });
    }

    async assignUserToAllSupers(currentUserId): Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                this.getAllChannels({ //mychannels
                    limit: '100',
                    show_empty: true,
                    show_member: true,
                    distinct_mode: 'nondistinct',
                    public_mode: PublicChannelFilter.PUBLIC,
                    super_mode: SuperChannelFilter.SUPER,
                    members_include_in: currentUserId,
                    custom_types: Status.ACTIVE,
                }).then((myChannels: any) => {
                    console.log('myChannels', myChannels);

                    this.getAllChannels({ // all public channels
                        limit: '100',
                        show_empty: true,
                        show_member: true,
                        distinct_mode: 'nondistinct',
                        public_mode: PublicChannelFilter.PUBLIC,
                        super_mode: SuperChannelFilter.SUPER,
                        custom_types: Status.ACTIVE,
                    }).then((allChannels: any) => {
                        if (allChannels) {
                            // let allChannels = data.results.channels;
                            console.log('allChannels', allChannels);

                            const channelsNotJoined = allChannels.filter(ac =>
                                !myChannels.some(mc => mc.channel_url === ac.channel_url)
                            );

                            console.log('channelsNotJoined', channelsNotJoined)
                            // channels = data.results.channels.filter(i => !i.members.find(k => k.user_id == currentUserId));
                            // console.log(channels)
                            // channelsNotJoined.forEach(async (i: any) => {
                            //     this.inviteUsers([currentUserId], i.members[0].user_id, i.channel_url)
                            // });
                            resolve();
                        }
                    }).catch((e) => {
                        reject(e);
                    });
                }).catch((e) => {
                    console.log(e);
                    reject();
                });
            }
            catch (e) {
                console.log(e);
                reject();
                // return [];
            }
        });
    }

    async assignAdminToAllPrivates(currentUserId): Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                this.getAllChannels({ //mychannels
                    limit: '100',
                    show_empty: true,
                    show_member: true,
                    distinct_mode: 'nondistinct',
                    public_mode: PublicChannelFilter.PRIVATE,
                    members_include_in: currentUserId,
                    custom_types: Status.ACTIVE,
                }).then((myChannels: any) => {
                    console.log('myChannels', myChannels);

                    this.getAllChannels({ // all private channels
                        limit: '100',
                        show_empty: true,
                        show_member: true,
                        distinct_mode: 'nondistinct',
                        public_mode: PublicChannelFilter.PRIVATE,
                        custom_types: Status.ACTIVE,
                    }).then((allChannels: any) => {
                        if (allChannels) {
                            // let allChannels = data.results.channels;
                            console.log('allChannels', allChannels);

                            const channelsNotJoined = allChannels.filter(ac =>
                                !myChannels.some(mc => mc.channel_url === ac.channel_url)
                            );

                            console.log('channelsNotJoined', channelsNotJoined)
                            // channels = data.results.channels.filter(i => !i.members.find(k => k.user_id == currentUserId));
                            // console.log(channels)
                            // channelsNotJoined.forEach(async (i: any) => {
                            //     this.inviteUsers([currentUserId], i.members[0].user_id, i.channel_url)
                            // });
                            resolve();
                        }
                    }).catch((e) => {
                        reject(e);
                    });
                }).catch((e) => {
                    console.log(e);
                    reject();
                });
            }
            catch (e) {
                console.log(e);
                reject();
                // return [];
            }
        });
    }

    async subscribeToMyChannels(): Promise<any> {
        let channels = await this.getChannels('', 100, 'Public', []);

        channels = channels.filter(i => !i.isDistinct);
        while (this.channelCollection?.hasMore) {
            let moreChannels = await this.channelCollection.loadMore();
            if (moreChannels?.length) {
                moreChannels = moreChannels.filter(i => !i.isDistinct);
                channels.push(...moreChannels);
            }
            else {
                break;
            }
        }
        // console.log('my channels', channels)
        return channels || [];
    }

    async getAllChannels(postData: any): Promise<any[]> {
        let allChannels: any[] = [];
        let nextToken: string | null = null;

        try {
            do {
                // If nextToken exists, add it to the request
                if (nextToken) {
                    postData.token = nextToken;
                }

                const data = await this.fetchChannels(postData);
                if (data?.status && data.results?.channels) {
                    allChannels.push(...data.results.channels);
                }

                // Update nextToken for pagination
                nextToken = data?.results?.next || null;
            } while (nextToken); // Continue until next is empty or undefined

            return allChannels;
        } catch (error) {
            console.error("Error fetching channels:", error);
            return [];
        }
    }

    private fetchChannels(postData): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('sendbird/user/listgroupchannel', '', postData, (data, error) => {
                if (data) {
                    resolve(data);
                } else {
                    reject(error);
                }
            });
        });
    }

    async getUnreadMessagesCount(): Promise<any> {
        return new Promise(async (resolve, reject) => {
            const isConnected = await this.ensureConnection();

            if (isConnected) {
                try {
                    const count = await this.sendbird.groupChannel.getTotalUnreadMessageCount(
                        {
                            channelCustomTypesFilter: ['Active']
                        }
                    );
                    resolve(count);
                }
                catch (e) {
                    reject(e);
                }
            }
            else {
                resolve(0);
            }
        });
    }

    getDndChannels(userId: string) {
        return new Promise(async (resolve, reject) => {
            this.requestService.postRequest('channel', 'get', {
                userId: userId,
            }, (data, error) => {
                if (data) {
                    // Ensure dndChannels is always an array
                    this.dndChannels = Array.isArray(data.results) ? data.results : [];
                    resolve(data.results);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    toggleDndChannel(userId: string, channelUrl: string, dnd: boolean) {
        return new Promise(async (resolve, reject) => {
            this.requestService.postRequest('channel', 'add', {
                userId: userId,
                channelId: channelUrl,
                dnd: dnd,
            }, (data, error) => {
                if (data) {
                    this.dndChannel.next({
                        userId: userId,
                        channelId: channelUrl,
                        dnd: dnd,
                    });
                    let channel = this.dndChannels.find(i => i.channelId == channelUrl);
                    if (channel) {
                        channel.dnd = dnd;
                    }
                    else {
                        this.dndChannels.push({
                            channelId: channelUrl,
                            dnd: dnd,
                            userId: userId,
                        });
                    }
                    resolve(data.results);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    getThreadedMessages(parent): Promise<any> {
        return new Promise(async (resolve, reject) => {
            const isConnected = await this.ensureConnection();

            if (isConnected) {
                try {
                    const threadedMessages = await this.getAllThreadedMessages(parent);
                    // console.log(threadedMessages);
                    parent.thread = threadedMessages;
                    // console.log(parent)
                    resolve(parent);
                }
                catch (e) {
                    reject(e);
                }
            }
        });
    }

    async getAllThreadedMessages(parentMessage: any): Promise<any[]> {
        const params = {
            prevResultSize: 0, // Adjust based on the API limits
            nextResultSize: 100, // Adjust based on the API limits
            isInclusive: false, // Include the parent message in the results
            reverse: false, // Fetch in ascending order of creation time
            includeParentMessageInfo: false, // Include details of the parent message
            includeReactions: true,
        };

        let allReplies: any[] = [];
        let hasMore = true;
        let timestamp = parentMessage.createdAt; // Starting point

        try {
            while (hasMore) {
                // Fetch threaded messages from the current timestamp
                const result = await parentMessage.getThreadedMessagesByTimestamp(timestamp, params);

                const threadedMessages = result?.threadedMessages || [];
                allReplies = allReplies.concat(threadedMessages);

                // Check if we need to continue fetching
                if (threadedMessages.length < params.nextResultSize) {
                    // No more messages to fetch
                    hasMore = false;
                } else {
                    // Update timestamp to the last message in the batch
                    timestamp = threadedMessages[threadedMessages.length - 1]?.createdAt;
                }
            }

            return allReplies; // Return all messages once the loop is complete
        } catch (error) {
            console.error("Error while fetching threaded messages:", error);
            throw error; // Re-throw the error for handling
        }
    }

    getChannelMembers(channelUrl: string, nextToken: string = '') {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest(
                'sendbird',
                'members/groups/list',
                {
                    channel_url: channelUrl,
                    limit: 100,
                    next: nextToken,
                },
                async (data: any, error: any) => {
                    if (data) {
                        let members = data.results.members;
                        if (data.results.next) {
                            try {
                                const nextMembers: any = await this.getChannelMembers(channelUrl, data.results.next);
                                resolve([...members, ...nextMembers]);
                            } catch (err) {
                                reject(err);
                            }
                        } else {
                            resolve(members);
                        }
                    } else {
                        reject(error);
                    }
                }
            );
        });
    }

    async getChannelsPendingApproval(limit: number = 20, order = GroupChannelListOrder.CHRONOLOGICAL): Promise<GroupChannel[]> {
        console.log('[SendbirdService] getChannelsPendingApproval called - using demo data', { limit, order });
        // Directly use demo data - no API calls
        try {
            const demoChannels = await this.demoDataService.getSendbirdChannelsPendingApprovalDemo(limit);
            // Create a mock collection object so hasMore works
            this.channelCollectionPendingApproval = {
                hasMore: false,
                loadMore: async () => [],
                setGroupChannelCollectionHandler: () => {},
                dispose: () => {}
            } as any;
            return this.adjustChannel(demoChannels);
        } catch (error) {
            console.error('[SendbirdService] getChannelsPendingApproval - Error getting demo data:', error);
            // Create a mock collection object so hasMore works
            this.channelCollectionPendingApproval = {
                hasMore: false,
                loadMore: async () => [],
                setGroupChannelCollectionHandler: () => {},
                dispose: () => {}
            } as any;
            return [];
        }
    }

    async loadMoreChannelsPendingApproval() {
        const isConnected = await this.ensureConnection();

        if (isConnected) {
            try {
                let channels: GroupChannel[] = [];
                if (this.channelCollectionPendingApproval?.hasMore) {
                    channels = await this.channelCollectionPendingApproval.loadMore();
                    // channels = this.updateMemberCount(channels);
                    channels = this.adjustChannel(channels);
                    // console.log('collection', channels);
                }
                return channels;
            }
            catch (e) {
                console.log(e);
                return [];
            }
        }
    }

    async deleteChannel(channelUrl: string, reason: string, creatorUserId: string) {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest(
                'sendbird',
                'group/channel/delete',
                {
                    channel_url: channelUrl,
                    reason: reason,
                    userId: creatorUserId,
                },
                (data: any, error: any) => {
                    if (data) {
                        resolve(data);
                    } else {
                        reject(error);
                    }
                }
            );
        });
    }

    async getMessageById(channelUrl: any, messageId: number) {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest(
                'sendbird',
                'user/getmessage',
                {
                    channel_url: channelUrl,
                    message_id: messageId,
                    channel_type: 'group_channels',
                },
                (data: any, error: any) => {
                    if (data) {
                        resolve(data);
                    } else {
                        reject(error);
                    }
                }
            );
        });
    }

    async getMessagesByIds(ids: string[], channelUrl: string): Promise<any[]> {
        let allMessages = [];
        await Promise.all(
            ids.map(id => this.sendbird.message.getMessage({ messageId: id, channelUrl: channelUrl, channelType: 'group' }).then(m => {
                allMessages.push(m);
            }))
        );
        return allMessages;
    }
}